import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import * as Yup from "yup";

import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { ATBuyerSellerDetails } from "../../atc-models/atc-org";
import useNotification from "../../../../shared/hooks/useNotification";
import { buyerSellerDetailsService } from "../../../v4/transaction-certificates/tc-service/buyer-seller-details.service";

// ------------------ Props ------------------
interface BuyerSellerDetailsFormProps {
  formRef: React.RefObject<any>;
  detailsData: ATBuyerSellerDetails;
  setDetailsData: (data: ATBuyerSellerDetails) => void;
}

// ------------------ Component ------------------
const BuyerSellerDetailsForm: React.FC<BuyerSellerDetailsFormProps> = ({
  formRef,
  detailsData,
  setDetailsData,
}) => {
  const internalFormRef = useRef<HTMLFormElement>(null);
  const { notify } = useNotification();

  // ------------------ State ------------------
  const [visible, setVisible] = useState({
    sellerName: true,
    buyerName: true,
    sellerTeIdLabel: true, 
    buyerTeIdLabel: true,
  });

  const [disable, setDisable] = useState({
    sellerAddress: true,
    sellerCbLicensingCode: true,
    buyerAddress: true,
    buyerScNumber: true,
  });

  // ------------------ Validation ------------------
  const validationSchema = Yup.object().shape({
    sellerTeId: Yup.string().required("Seller TE ID is required"),
    sellerAddress: Yup.string().required("Seller Address is required"),
    sellerName: Yup.string().required("Seller Name is required"),
    sellerCbLicensingCode: Yup.string().required(
      "Seller CB Licensing Code is required"
    ),
    buyerTeId: Yup.string().required("Buyer TE ID is required"),
    buyerAddress: Yup.string().required("Buyer Address is required"),
    buyerName: Yup.string().required("Buyer Name is required"),
    buyerScNumber: Yup.string().required("Buyer SC Number is required"),
  });

  // ------------------ API ------------------
  const fetchTEDetailsFromAPI = async (
    value: string,
    offset: number,
    type: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };

  // ------------------ Seller Handlers ------------------
  const handleSellerTEIdValueChange = (value: any) => {
    if (value == null) {
      // Reset
      [
        "sellerTeId",
        "sellerName",
        "sellerAddress",
        "sellerCbLicensingCode",
      ].forEach((field) => internalFormRef.current?.setFieldValue(field, ""));

      setDisable((prev) => ({
        ...prev,
        sellerName: false,
        sellerAddress: false,
        sellerCbLicensingCode: false,
      }));

      setVisible((prev) => ({
        ...prev,
        sellerName: true,
        sellerTeIdLabel: true,
      }));
    } else {
      // Populate
      internalFormRef.current?.setFieldValue("sellerTeId", value.TE_ID);
      internalFormRef.current?.setFieldValue("sellerName", value.account_name);
      internalFormRef.current?.setFieldValue(
        "sellerAddress",
        value.street || ""
      );
      internalFormRef.current?.setFieldValue(
        "sellerCbLicensingCode",
        value.cb_licensing_code || ""
      );

      setDisable((prev) => ({
        ...prev,
        sellerName: Boolean(value.account_name),
        sellerAddress: Boolean(value.street),
        sellerCbLicensingCode: false, // always editable
      }));

      setVisible((prev) => ({ ...prev, sellerName: false }));
    }
  };

  const handleSellerNamedValueChange = (value: any) => {
    if (value == null) {
      [
        "sellerAddress",
        "sellerName",
        "sellerTeId",
        "sellerCbLicensingCode",
      ].forEach((field) => internalFormRef.current?.setFieldValue(field, ""));

      setDisable((prev) => ({
        ...prev,
        sellerAddress: false,
        sellerCbLicensingCode: false,
      }));
      setVisible((prev) => ({ ...prev, sellerTeIdLabel: true }));
    } else {
      internalFormRef.current?.setFieldValue("sellerName", value.account_name);
      internalFormRef.current?.setFieldValue("sellerTeId", value.TE_ID);

      // Address
      if (value.street) {
        internalFormRef.current?.setFieldValue("sellerAddress", value.street);
        setDisable((prev) => ({ ...prev, sellerAddress: true }));
      } else {
        internalFormRef.current?.setFieldValue("sellerAddress", "");
        setDisable((prev) => ({ ...prev, sellerAddress: false }));
      }

      // Licensing Code
      if (value.cb_licensing_code) {
        internalFormRef.current?.setFieldValue(
          "sellerCbLicensingCode",
          value.cb_licensing_code
        );
        setDisable((prev) => ({ ...prev, sellerCbLicensingCode: true }));
      } else {
        internalFormRef.current?.setFieldValue("sellerCbLicensingCode", "");
        setDisable((prev) => ({ ...prev, sellerCbLicensingCode: false }));
      }

      setVisible((prev) => ({ ...prev, sellerTeIdLabel: false }));
    }
  };

  // ------------------ Buyer Handlers ------------------
  const handleBuyerTEIdValueChange = (value: any) => {
    if (value == null) {
      ["buyerTeId", "buyerName", "buyerAddress", "buyerScNumber"].forEach(
        (field) => internalFormRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: false,
        buyerAddress: false,
        buyerScNumber: false,
      }));
      setVisible((prev) => ({
        ...prev,
        buyerName: true,
        buyerTeIdLabel: true,
      }));
    } else {
      internalFormRef.current?.setFieldValue("buyerTeId", value.TE_ID);
      internalFormRef.current?.setFieldValue("buyerName", value.account_name);
      internalFormRef.current?.setFieldValue(
        "buyerAddress",
        value.street || ""
      );
      internalFormRef.current?.setFieldValue(
        "buyerScNumber",
        value.sc_number || ""
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: Boolean(value.account_name),
        buyerAddress: Boolean(value.street),
        buyerScNumber: false, // always editable
      }));

      setVisible((prev) => ({ ...prev, buyerName: false }));
    }
  };

  const handleBuyerNameValueChange = (value: any) => {
    if (value == null) {
      ["buyerAddress", "buyerName", "buyerTeId", "buyerScNumber"].forEach(
        (field) => internalFormRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerAddress: false,
        buyerScNumber: false,
      }));
      setVisible((prev) => ({ ...prev, buyerTeIdLabel: true }));
    } else {
      internalFormRef.current?.setFieldValue("buyerName", value.account_name);
      internalFormRef.current?.setFieldValue("buyerTeId", value.TE_ID);

      // Address
      if (value.street) {
        internalFormRef.current?.setFieldValue("buyerAddress", value.street);
        setDisable((prev) => ({ ...prev, buyerAddress: true }));
      } else {
        internalFormRef.current?.setFieldValue("buyerAddress", "");
        setDisable((prev) => ({ ...prev, buyerAddress: false }));
      }

      // SC Number
      if (value.sc_number) {
        internalFormRef.current?.setFieldValue(
          "buyerScNumber",
          value.sc_number
        );
        setDisable((prev) => ({ ...prev, buyerScNumber: true }));
      } else {
        internalFormRef.current?.setFieldValue("buyerScNumber", "");
        setDisable((prev) => ({ ...prev, buyerScNumber: false }));
      }

      setVisible((prev) => ({ ...prev, buyerTeIdLabel: false }));
    }
  };

  // ------------------ Form Config ------------------
  const formConfig = {
    pageName: "buyer-seller-details",
    initialValues: detailsData,
    validationSchema,
    enableReinitialize: true,
    sections: [
      {
        title: "Seller Details",
        fields: [
          // Seller TE ID (Search with clear)
          {
            label: "Seller TE-ID",
            name: "sellerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerTeId,
            visible: () => true,
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              param: "SellerTEId",
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Seller Name (Search / Text)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerName,
            visible: () => visible.sellerName,
            props: {
              placeholder: "Search Seller Name",
              variant: "outlined",
              onChange: handleSellerNamedValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          {
            label: "Seller Name",
            name: "sellerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => !visible.sellerName,
            props: {
              placeholder: "Enter Seller Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Address + Licensing
          {
            label: "Seller Address",
            name: "sellerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerAddress,
            disabled: disable.sellerAddress,
            props: {
              placeholder: "Enter Seller Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "CB Licensing Code",
            name: "sellerCbLicensingCode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerCbLicensingCode,
            disabled: false,
            props: {
              placeholder: "Enter CB Licensing Code",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
      {
        title: "Buyer Details",
        fields: [
          // Buyer TE ID (Search with clear)
          {
            label: "Buyer TE-ID",
            name: "buyerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerTeId,
            visible: () => true,
            props: {
              placeholder: "Search Buyer TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              onChange: handleBuyerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Buyer Name (Search / Text)
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerName,
            visible: () => visible.buyerName,
            props: {
              placeholder: "Search Buyer Name",
              variant: "outlined",
              onChange: handleBuyerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => !visible.buyerName,
            props: {
              placeholder: "Enter Buyer Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Address + SC Number
          {
            label: "Buyer Address",
            name: "buyerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerAddress,
            disabled: disable.buyerAddress,
            props: {
              placeholder: "Enter Buyer Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Buyer SC Number",
            name: "buyerScNumber",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerScNumber,
            disabled: false,
            props: {
              placeholder: "Enter SC Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => setDetailsData(values),
  };

  // ------------------ Expose Methods ------------------
  const validateForm = async () =>
    internalFormRef.current?.validateForm() || {};
  const submitForm = async () => internalFormRef.current?.submitForm();

  React.useImperativeHandle(formRef, () => ({
    validateForm,
    submitForm,
    values: detailsData,
  }));

  // ------------------ Render ------------------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 6, fontWeight: "bold" }}>
        Buyer & Seller Details
      </Typography>

      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={internalFormRef}
      />
    </Box>
  );
};

export default BuyerSellerDetailsForm;
